import { PlusOutlined } from '@ant-design/icons';
import { PERMISSIONS, ROLES } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Avatar,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { api, errorText } from '../../api/client';
import { ROLE_LABEL, type StaffUser, type ZaloOaStatus } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

interface Follower {
  user_id: string;
  display_name: string | null;
  avatar: string | null;
}

/** Nhân viên: vai trò, khóa/mở, liên kết Zalo OA để nhận thông báo yêu cầu mới (D-12). */
export function UsersPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { me, can } = useAuth();
  const canManage = can(PERMISSIONS.USERS_MANAGE);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<StaffUser | null>(null);
  const [resetFor, setResetFor] = useState<StaffUser | null>(null);
  const [form] = Form.useForm();
  const [pwForm] = Form.useForm();

  const users = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.page<StaffUser>('/admin/users', { limit: 100 })).items,
  });
  const oa = useQuery({
    queryKey: ['zalo-oa'],
    queryFn: () => api.get<ZaloOaStatus>('/admin/integrations/zalo-oa'),
    enabled: can(PERMISSIONS.SETTINGS_READ),
  });
  const followers = useQuery({
    queryKey: ['zalo-followers'],
    queryFn: () =>
      api.get<{ total: number; users: Follower[] }>('/admin/integrations/zalo-oa/followers', {
        offset: 0,
      }),
    enabled: canManage && editing !== null && !!oa.data?.connected,
    retry: false,
  });
  const grantable = ROLES.filter((r) => r !== 'super_admin' || me?.roles.includes('super_admin'));

  const patch = async (u: StaffUser, body: Record<string, unknown>) => {
    try {
      await api.patch(`/admin/users/${u.id}`, body);
      void qc.invalidateQueries({ queryKey: ['users'] });
      message.success('Đã lưu');
      return true;
    } catch (e) {
      message.error(errorText(e));
      return false;
    }
  };

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Nhân viên
        </Typography.Title>
      }
      extra={
        canManage && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => (form.resetFields(), setCreating(true))}
          >
            Thêm nhân viên
          </Button>
        )
      }
    >
      {oa.data && !oa.data.connected && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Chưa kết nối Zalo OA – nhân viên sẽ chỉ thấy yêu cầu mới qua chuông trên Admin."
        />
      )}
      <Table<StaffUser>
        rowKey="id"
        loading={users.isLoading}
        dataSource={users.data}
        pagination={false}
        columns={[
          {
            title: 'Họ tên',
            render: (_, u) => (
              <Space direction="vertical" size={0}>
                <span>{u.full_name}</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {u.email}
                </Typography.Text>
              </Space>
            ),
          },
          { title: 'SĐT', dataIndex: 'phone', render: (v) => v ?? '—' },
          {
            title: 'Vai trò',
            render: (_, u) => u.roles.map((r) => <Tag key={r}>{ROLE_LABEL[r] ?? r}</Tag>),
          },
          {
            title: 'Zalo OA',
            render: (_, u) =>
              u.zalo_oa_user_id ? <Tag color="blue">Đã liên kết</Tag> : <Tag>Chưa</Tag>,
          },
          {
            title: 'Nhận TB yêu cầu mới',
            render: (_, u) => (
              <Switch
                size="small"
                checked={u.notify_new_requests}
                disabled={!canManage}
                onChange={(v) => void patch(u, { notify_new_requests: v })}
              />
            ),
          },
          {
            title: 'Đăng nhập gần nhất',
            render: (_, u) =>
              u.last_login_at ? dayjs(u.last_login_at).format('DD/MM HH:mm') : '—',
          },
          {
            title: 'Trạng thái',
            render: (_, u) => (
              <Space>
                {u.status === 'active' ? (
                  <Tag color="green">Hoạt động</Tag>
                ) : (
                  <Tag color="red">Đã khóa</Tag>
                )}
                {u.locked_until && dayjs(u.locked_until).isAfter(dayjs()) && (
                  <Tag color="orange">Tạm khóa do sai mật khẩu</Tag>
                )}
              </Space>
            ),
          },
          ...(canManage
            ? [
                {
                  title: '',
                  render: (_: unknown, u: StaffUser) => (
                    <Space>
                      <Button size="small" onClick={() => (form.setFieldsValue(u), setEditing(u))}>
                        Sửa
                      </Button>
                      <Button size="small" onClick={() => (pwForm.resetFields(), setResetFor(u))}>
                        Đặt lại MK
                      </Button>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />

      <Modal
        open={creating}
        title="Thêm nhân viên"
        okText="Tạo"
        onCancel={() => setCreating(false)}
        forceRender
        onOk={async () => {
          const v = await form.validateFields();
          try {
            await api.post('/admin/users', v);
            message.success('Đã tạo tài khoản');
            setCreating(false);
            void qc.invalidateQueries({ queryKey: ['users'] });
          } catch (e) {
            message.error(errorText(e));
          }
        }}
      >
        {creating && (
          <Form form={form} layout="vertical">
            <Form.Item name="full_name" label="Họ tên" rules={[{ required: true, min: 2 }]}>
              <Input />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email đăng nhập"
              rules={[{ required: true, type: 'email' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="phone" label="SĐT">
              <Input />
            </Form.Item>
            <Form.Item
              name="password"
              label="Mật khẩu ban đầu"
              rules={[{ required: true, min: 10 }]}
              extra="Tối thiểu 10 ký tự, có chữ và số."
            >
              <Input.Password />
            </Form.Item>
            <Form.Item name="roles" label="Vai trò" rules={[{ required: true }]}>
              <Select
                mode="multiple"
                options={grantable.map((r) => ({ value: r, label: ROLE_LABEL[r] ?? r }))}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        open={editing !== null}
        title={editing?.full_name}
        okText="Lưu"
        onCancel={() => setEditing(null)}
        forceRender
        width={600}
        onOk={async () => {
          if (!editing) return;
          const v = await form.validateFields();
          if (
            await patch(editing, {
              full_name: v.full_name,
              phone: v.phone || null,
              roles: v.roles,
              status: v.status,
              zalo_oa_user_id: v.zalo_oa_user_id || null,
              notify_new_requests: v.notify_new_requests,
            })
          )
            setEditing(null);
        }}
      >
        {editing && (
          <Form form={form} layout="vertical">
            <Form.Item name="full_name" label="Họ tên" rules={[{ required: true, min: 2 }]}>
              <Input />
            </Form.Item>
            <Form.Item name="phone" label="SĐT">
              <Input />
            </Form.Item>
            <Form.Item name="roles" label="Vai trò" rules={[{ required: true }]}>
              <Select
                mode="multiple"
                options={grantable.map((r) => ({ value: r, label: ROLE_LABEL[r] ?? r }))}
              />
            </Form.Item>
            <Form.Item name="status" label="Trạng thái">
              <Select
                disabled={editing.id === me?.id}
                options={[
                  { value: 'active', label: 'Hoạt động' },
                  { value: 'disabled', label: 'Khóa tài khoản' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="zalo_oa_user_id"
              label="Tài khoản Zalo (người quan tâm OA Navicosa)"
              extra="Nhân viên cần bấm Quan tâm OA Navicosa trên Zalo trước, rồi chọn tên ở đây. Tin tư vấn chỉ gửi được trong 7 ngày kể từ lần cuối nhân viên tương tác với OA."
            >
              {oa.data?.connected ? (
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={followers.isLoading}
                  notFoundContent={followers.isError ? errorText(followers.error) : undefined}
                  options={[
                    ...(followers.data?.users ?? []).map((f) => ({
                      value: f.user_id,
                      label: f.display_name ?? f.user_id,
                      avatar: f.avatar,
                    })),
                    ...(editing.zalo_oa_user_id &&
                    !followers.data?.users.some((f) => f.user_id === editing.zalo_oa_user_id)
                      ? [
                          {
                            value: editing.zalo_oa_user_id,
                            label: editing.zalo_oa_user_id,
                            avatar: null,
                          },
                        ]
                      : []),
                  ]}
                  optionRender={(o) => (
                    <Space>
                      <Avatar size="small" src={o.data.avatar ?? undefined}>
                        {String(o.data.label).charAt(0)}
                      </Avatar>
                      {o.data.label}
                    </Space>
                  )}
                />
              ) : (
                <Input placeholder="user_id (khi chưa kết nối OA có thể nhập tay)" />
              )}
            </Form.Item>
            <Form.Item
              name="notify_new_requests"
              label="Nhận thông báo yêu cầu mới qua Zalo"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        open={resetFor !== null}
        title={`Đặt lại mật khẩu – ${resetFor?.full_name}`}
        okText="Đặt lại"
        onCancel={() => setResetFor(null)}
        forceRender
        onOk={async () => {
          if (!resetFor) return;
          const v = await pwForm.validateFields();
          try {
            await api.post(`/admin/users/${resetFor.id}/reset-password`, { password: v.password });
            message.success('Đã đặt lại – các phiên đăng nhập cũ đã bị thu hồi');
            setResetFor(null);
          } catch (e) {
            message.error(errorText(e));
          }
        }}
      >
        <Form form={pwForm} layout="vertical">
          <Form.Item name="password" label="Mật khẩu mới" rules={[{ required: true, min: 10 }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
